import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { WizardShell } from '../components/WizardShell';
import { TextField } from '../components/form/TextField';
import { PhoneField } from '../components/form/PhoneField';
import { SelectField } from '../components/form/SelectField';
import { RadioGroup } from '../components/form/RadioGroup';
import { CheckboxField } from '../components/form/CheckboxField';
import { YesNoToggle } from '../components/form/YesNoToggle';
import { CurrencyField } from '../components/form/CurrencyField';
import { PercentField } from '../components/form/PercentField';
import { DateField } from '../components/form/DateField';
import { SectionCard } from '../components/form/SectionCard';
import { FieldGrid } from '../components/form/FieldGrid';
import { ApplicationProvider, useApplicationState, calculateCompletion } from '../state/ApplicationContext';
import { CrmHeader } from '../components/application/sections/CrmHeader';
import { ClientGoalsSection } from '../components/application/sections/ClientGoalsSection';
import { BorrowerManagementSection } from '../components/application/sections/BorrowerManagementSection';
const STEPS = [{
  id: 'goals',
  label: 'Client Goals'
}, {
  id: 'borrowers_dynamic',
  label: 'Borrowers & Title'
}, {
  id: 'employment',
  label: 'Employment & Income'
}, {
  id: 'property',
  label: 'Subject Property'
}, {
  id: 'mortgage',
  label: 'Current Mortgage'
}, {
  id: 'credit',
  label: 'Credit & Assets'
}, {
  id: 'review',
  label: 'Review & Submit'
}];
const US_STATES = [{
  value: 'AL',
  label: 'Alabama'
}, {
  value: 'AK',
  label: 'Alaska'
}, {
  value: 'AZ',
  label: 'Arizona'
}, {
  value: 'AR',
  label: 'Arkansas'
}, {
  value: 'CA',
  label: 'California'
}, {
  value: 'CO',
  label: 'Colorado'
}, {
  value: 'CT',
  label: 'Connecticut'
}, {
  value: 'DE',
  label: 'Delaware'
}, {
  value: 'FL',
  label: 'Florida'
}, {
  value: 'GA',
  label: 'Georgia'
}, {
  value: 'HI',
  label: 'Hawaii'
}, {
  value: 'ID',
  label: 'Idaho'
}, {
  value: 'IL',
  label: 'Illinois'
}, {
  value: 'IN',
  label: 'Indiana'
}, {
  value: 'IA',
  label: 'Iowa'
}, {
  value: 'KS',
  label: 'Kansas'
}, {
  value: 'KY',
  label: 'Kentucky'
}, {
  value: 'LA',
  label: 'Louisiana'
}, {
  value: 'ME',
  label: 'Maine'
}, {
  value: 'MD',
  label: 'Maryland'
}, {
  value: 'MA',
  label: 'Massachusetts'
}, {
  value: 'MI',
  label: 'Michigan'
}, {
  value: 'MN',
  label: 'Minnesota'
}, {
  value: 'MS',
  label: 'Mississippi'
}, {
  value: 'MO',
  label: 'Missouri'
}, {
  value: 'MT',
  label: 'Montana'
}, {
  value: 'NE',
  label: 'Nebraska'
}, {
  value: 'NV',
  label: 'Nevada'
}, {
  value: 'NH',
  label: 'New Hampshire'
}, {
  value: 'NJ',
  label: 'New Jersey'
}, {
  value: 'NM',
  label: 'New Mexico'
}, {
  value: 'NY',
  label: 'New York'
}, {
  value: 'NC',
  label: 'North Carolina'
}, {
  value: 'ND',
  label: 'North Dakota'
}, {
  value: 'OH',
  label: 'Ohio'
}, {
  value: 'OK',
  label: 'Oklahoma'
}, {
  value: 'OR',
  label: 'Oregon'
}, {
  value: 'PA',
  label: 'Pennsylvania'
}, {
  value: 'RI',
  label: 'Rhode Island'
}, {
  value: 'SC',
  label: 'South Carolina'
}, {
  value: 'SD',
  label: 'South Dakota'
}, {
  value: 'TN',
  label: 'Tennessee'
}, {
  value: 'TX',
  label: 'Texas'
}, {
  value: 'UT',
  label: 'Utah'
}, {
  value: 'VT',
  label: 'Vermont'
}, {
  value: 'VA',
  label: 'Virginia'
}, {
  value: 'WA',
  label: 'Washington'
}, {
  value: 'WV',
  label: 'West Virginia'
}, {
  value: 'WI',
  label: 'Wisconsin'
}, {
  value: 'WY',
  label: 'Wyoming'
}];
function RefinanceApplicationContent() {
  const navigate = useNavigate();
  const screenInit = useScreenInit();
  const {
    state: appState
  } = useApplicationState();
  const completionPercentage = calculateCompletion(appState);
  const [currentStep, setCurrentStep] = useState(screenInit.currentStep ?? 0);
  const [submitted, setSubmitted] = useState(false);
  // Borrower Info
  const [isVeteran, setIsVeteran] = useState<boolean | null>(null);
  const [disabilityPercent, setDisabilityPercent] = useState('');
  const [email, setEmail] = useState('');
  const [borrowerName, setBorrowerName] = useState('');
  const [isMarried, setIsMarried] = useState<boolean | null>(null);
  const [ssn, setSsn] = useState('');
  const [dob, setDob] = useState('');
  const [creditScore, setCreditScore] = useState('');
  const [homePhone, setHomePhone] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [workPhone, setWorkPhone] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [county, setCounty] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertyCity, setPropertyCity] = useState('');
  const [propertyState, setPropertyState] = useState('');
  const [propertyZip, setPropertyZip] = useState('');
  const [propertyCounty, setPropertyCounty] = useState('');
  // Employment
  const [employerName, setEmployerName] = useState('');
  const [position, setPosition] = useState('');
  const [yearsEmployed, setYearsEmployed] = useState('');
  const [income, setIncome] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [prevEmployerName, setPrevEmployerName] = useState('');
  const [prevPosition, setPrevPosition] = useState('');
  const [prevYearsEmployed, setPrevYearsEmployed] = useState('');
  const [prevIncome, setPrevIncome] = useState('');
  const [prevEmploymentType, setPrevEmploymentType] = useState('');
  const [ssi, setSsi] = useState('');
  const [pension, setPension] = useState('');
  const [vaDisability, setVaDisability] = useState('');
  const [additionalIncome, setAdditionalIncome] = useState('');
  const [nextPayRaise, setNextPayRaise] = useState('');
  const [creditCards, setCreditCards] = useState('');
  const [autoLoans, setAutoLoans] = useState('');
  const [personalLoans, setPersonalLoans] = useState('');
  const [otherDebt, setOtherDebt] = useState('');
  // Co-Borrower
  const [noCoBorrower, setNoCoBorrower] = useState(false);
  const [coBorrowerName, setCoBorrowerName] = useState('');
  const [coBorrowerMarried, setCoBorrowerMarried] = useState<boolean | null>(null);
  const [coBorrowerSsn, setCoBorrowerSsn] = useState('');
  const [coBorrowerDob, setCoBorrowerDob] = useState('');
  const [coBorrowerCreditScore, setCoBorrowerCreditScore] = useState('');
  const [coBorrowerEmployer, setCoBorrowerEmployer] = useState('');
  const [coBorrowerPosition, setCoBorrowerPosition] = useState('');
  const [coBorrowerYears, setCoBorrowerYears] = useState('');
  const [coBorrowerIncome, setCoBorrowerIncome] = useState('');
  const [coBorrowerPrevEmployer, setCoBorrowerPrevEmployer] = useState('');
  const [coBorrowerPrevPosition, setCoBorrowerPrevPosition] = useState('');
  const [coBorrowerPrevYears, setCoBorrowerPrevYears] = useState('');
  const [coBorrowerPrevIncome, setCoBorrowerPrevIncome] = useState('');
  const [coBorrowerSsi, setCoBorrowerSsi] = useState('');
  const [coBorrowerPension, setCoBorrowerPension] = useState('');
  const [coBorrowerVaDisability, setCoBorrowerVaDisability] = useState('');
  const [coBorrowerAdditionalIncome, setCoBorrowerAdditionalIncome] = useState('');
  const [coBorrowerNextPayRaise, setCoBorrowerNextPayRaise] = useState('');
  // Property
  const [occupancy, setOccupancy] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');
  const [squareFootage, setSquareFootage] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [condoApproved, setCondoApproved] = useState<boolean | null>(null);
  // Current Mortgage
  const [lastRefinanceDate, setLastRefinanceDate] = useState('');
  const [purchaseYear, setPurchaseYear] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [mortgageCompany, setMortgageCompany] = useState('');
  const [currentBalance, setCurrentBalance] = useState('');
  const [currentPayment, setCurrentPayment] = useState('');
  const [clientEstimatedValue, setClientEstimatedValue] = useState('');
  const [escrowed, setEscrowed] = useState<boolean | null>(null);
  const [propertyTaxes, setPropertyTaxes] = useState('');
  const [insurance, setInsurance] = useState('');
  const [pmi, setPmi] = useState('');
  const [hoa, setHoa] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [loanStructure, setLoanStructure] = useState('');
  const [loanType, setLoanType] = useState('');
  const [hasSecondMortgage, setHasSecondMortgage] = useState<boolean | null>(null);
  const [secondLender, setSecondLender] = useState('');
  const [secondBalance, setSecondBalance] = useState('');
  const [secondYearOpened, setSecondYearOpened] = useState('');
  const [secondPayment, setSecondPayment] = useState('');
  const [secondRate, setSecondRate] = useState('');
  const [secondTerm, setSecondTerm] = useState('');
  const [secondStructure, setSecondStructure] = useState('');
  const [helocCashOut, setHelocCashOut] = useState<boolean | null>(null);
  // Credit & Assets
  const [foreclosure, setForeclosure] = useState<boolean | null>(null);
  const [bankruptcy, setBankruptcy] = useState<boolean | null>(null);
  const [bankruptcyChapter, setBankruptcyChapter] = useState('');
  const [childSupport, setChildSupport] = useState<boolean | null>(null);
  const [loanModification, setLoanModification] = useState<boolean | null>(null);
  const [deferredInterest, setDeferredInterest] = useState<boolean | null>(null);
  const [solarPanels, setSolarPanels] = useState<boolean | null>(null);
  const [forbearance, setForbearance] = useState<boolean | null>(null);
  const [liens, setLiens] = useState<boolean | null>(null);
  const [lateMortgage, setLateMortgage] = useState<boolean | null>(null);
  const [farmActivity, setFarmActivity] = useState<boolean | null>(null);
  const [reinstated, setReinstated] = useState<boolean | null>(null);
  const [amountOwed, setAmountOwed] = useState('');
  const [dateOwed, setDateOwed] = useState('');
  const [assetSummary, setAssetSummary] = useState('');
  const [vaRelativeName, setVaRelativeName] = useState('');
  const [vaRelativeAddress, setVaRelativeAddress] = useState('');
  const [vaRelativePhone, setVaRelativePhone] = useState('');
  const [vaRelativeRelationship, setVaRelativeRelationship] = useState('');
  const [waterSource, setWaterSource] = useState('');
  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      const nextIndex = currentStep + 1;
      setCurrentStep(nextIndex);
      document.getElementById(STEPS[nextIndex].id)?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      const prevIndex = currentStep - 1;
      setCurrentStep(prevIndex);
      document.getElementById(STEPS[prevIndex].id)?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleSubmit = () => {
    setSubmitted(true);
  };
  if (submitted) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-463">
        <div className="max-w-2xl w-full bg-white rounded-2xl p-8 text-center shadow-lg" data-id="element-464">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-465">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-466">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-467" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-468">
            Application Submitted!
          </h1>
          <p className="text-gray-600 mb-8" data-id="element-469">
            Your refinance mortgage application has been successfully submitted.
            Our team will review your information and contact you within 24-48
            hours.
          </p>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700" data-id="element-470">
            Return to Home
          </button>
        </div>
      </div>;
  }
  const getStepContent = (index: number) => {
    const stepId = STEPS[index].id;
    if (stepId === 'goals') return <ClientGoalsSection data-id="element-471" />;
    if (stepId === 'borrowers_dynamic') return <BorrowerManagementSection data-id="element-472" />;
    const legacyCaseById: Record<string, number> = {
      employment: 1,
      property: 3,
      mortgage: 4,
      credit: 5,
      review: 6
    };
    switch (legacyCaseById[stepId]) {
      case 0:
        // Borrower Information (legacy — no longer reachable)
        return <div className="space-y-6" data-id="element-473">
            <SectionCard title="Personal Details" data-id="element-474">
              <div className="space-y-4" data-id="element-475">
                <FieldGrid data-id="element-476">
                  <YesNoToggle label="Veteran" value={isVeteran} onChange={setIsVeteran} data-id="element-477" />
                  <PercentField label="Disability Percentage" value={disabilityPercent} onChange={setDisabilityPercent} data-id="element-478" />
                </FieldGrid>
                <TextField label="Email Address" type="email" value={email} onChange={setEmail} required data-id="element-479" />
                <FieldGrid data-id="element-480">
                  <TextField label="Borrower Name" value={borrowerName} onChange={setBorrowerName} required data-id="element-481" />
                  <YesNoToggle label="Married" value={isMarried} onChange={setIsMarried} data-id="element-482" />
                </FieldGrid>
                <FieldGrid data-id="element-483">
                  <TextField label="SSN" value={ssn} onChange={setSsn} placeholder="XXX-XX-XXXX" required data-id="element-484" />
                  <DateField label="Date of Birth" value={dob} onChange={setDob} required data-id="element-485" />
                </FieldGrid>
                <TextField label="Credit Score" value={creditScore} onChange={setCreditScore} data-id="element-486" />
              </div>
            </SectionCard>

            <SectionCard title="Contact Information" data-id="element-487">
              <FieldGrid data-id="element-488">
                <PhoneField label="Home Phone" value={homePhone} onChange={setHomePhone} data-id="element-489" />
                <PhoneField label="Cell Phone" value={cellPhone} onChange={setCellPhone} required data-id="element-490" />
                <PhoneField label="Work Phone" value={workPhone} onChange={setWorkPhone} data-id="element-491" />
              </FieldGrid>
            </SectionCard>

            <SectionCard title="Current Address" data-id="element-492">
              <div className="space-y-4" data-id="element-493">
                <TextField label="Street Address" value={street} onChange={setStreet} required data-id="element-494" />
                <FieldGrid data-id="element-495">
                  <TextField label="City" value={city} onChange={setCity} required data-id="element-496" />
                  <SelectField label="State" value={state} onChange={setState} options={US_STATES} required data-id="element-497" />
                </FieldGrid>
                <FieldGrid data-id="element-498">
                  <TextField label="Zip Code" value={zip} onChange={setZip} required data-id="element-499" />
                  <TextField label="County" value={county} onChange={setCounty} data-id="element-500" />
                </FieldGrid>
              </div>
            </SectionCard>

            <SectionCard title="Subject Property" data-id="element-501">
              <div className="space-y-4" data-id="element-502">
                <TextField label="Property Address" value={propertyAddress} onChange={setPropertyAddress} required data-id="element-503" />
                <FieldGrid data-id="element-504">
                  <TextField label="City" value={propertyCity} onChange={setPropertyCity} required data-id="element-505" />
                  <SelectField label="State" value={propertyState} onChange={setPropertyState} options={US_STATES} required data-id="element-506" />
                </FieldGrid>
                <FieldGrid data-id="element-507">
                  <TextField label="Zip Code" value={propertyZip} onChange={setPropertyZip} required data-id="element-508" />
                  <TextField label="County" value={propertyCounty} onChange={setPropertyCounty} data-id="element-509" />
                </FieldGrid>
              </div>
            </SectionCard>
          </div>;
      case 1:
        // Employment & Income
        return <div className="space-y-6" data-id="element-510">
            <SectionCard title="Current Employer" data-id="element-511">
              <div className="space-y-4" data-id="element-512">
                <FieldGrid data-id="element-513">
                  <TextField label="Employer Name" value={employerName} onChange={setEmployerName} required data-id="element-514" />
                  <TextField label="Position / Title" value={position} onChange={setPosition} required data-id="element-515" />
                </FieldGrid>
                <FieldGrid data-id="element-516">
                  <TextField label="Years Employed" value={yearsEmployed} onChange={setYearsEmployed} required data-id="element-517" />
                  <CurrencyField label="Annual Income" value={income} onChange={setIncome} required data-id="element-518" />
                </FieldGrid>
                <RadioGroup label="Employment Type" value={employmentType} onChange={setEmploymentType} options={[{
                value: 'W2',
                label: 'W2'
              }, {
                value: '1099',
                label: '1099'
              }, {
                value: 'Self Employed',
                label: 'Self Employed'
              }]} layout="horizontal" required data-id="element-519" />
              </div>
            </SectionCard>

            <SectionCard title="Previous Employer" data-id="element-520">
              <div className="space-y-4" data-id="element-521">
                <FieldGrid data-id="element-522">
                  <TextField label="Employer Name" value={prevEmployerName} onChange={setPrevEmployerName} data-id="element-523" />
                  <TextField label="Position / Title" value={prevPosition} onChange={setPrevPosition} data-id="element-524" />
                </FieldGrid>
                <FieldGrid data-id="element-525">
                  <TextField label="Years Employed" value={prevYearsEmployed} onChange={setPrevYearsEmployed} data-id="element-526" />
                  <CurrencyField label="Annual Income" value={prevIncome} onChange={setPrevIncome} data-id="element-527" />
                </FieldGrid>
                <RadioGroup label="Employment Type" value={prevEmploymentType} onChange={setPrevEmploymentType} options={[{
                value: 'W2',
                label: 'W2'
              }, {
                value: '1099',
                label: '1099'
              }, {
                value: 'Self Employed',
                label: 'Self Employed'
              }]} layout="horizontal" data-id="element-528" />
              </div>
            </SectionCard>

            <SectionCard title="Additional Income" data-id="element-529">
              <FieldGrid data-id="element-530">
                <CurrencyField label="SSI" value={ssi} onChange={setSsi} data-id="element-531" />
                <CurrencyField label="Pension" value={pension} onChange={setPension} data-id="element-532" />
                <CurrencyField label="VA Disability" value={vaDisability} onChange={setVaDisability} data-id="element-533" />
                <CurrencyField label="Additional Income" value={additionalIncome} onChange={setAdditionalIncome} data-id="element-534" />
              </FieldGrid>
              <div className="mt-4" data-id="element-535">
                <TextField label="Next Pay Raise" value={nextPayRaise} onChange={setNextPayRaise} data-id="element-536" />
              </div>
            </SectionCard>

            <SectionCard title="Debts" data-id="element-537">
              <FieldGrid data-id="element-538">
                <CurrencyField label="Credit Cards (Monthly)" value={creditCards} onChange={setCreditCards} data-id="element-539" />
                <CurrencyField label="Auto Loans (Monthly)" value={autoLoans} onChange={setAutoLoans} data-id="element-540" />
                <CurrencyField label="Personal Loans (Monthly)" value={personalLoans} onChange={setPersonalLoans} data-id="element-541" />
                <CurrencyField label="Other Debt (Monthly)" value={otherDebt} onChange={setOtherDebt} data-id="element-542" />
              </FieldGrid>
            </SectionCard>
          </div>;
      case 2:
        // Co-Borrower
        return <div className="space-y-6" data-id="element-543">
            <SectionCard data-id="element-544">
              <CheckboxField label="No Co-Borrower" checked={noCoBorrower} onChange={setNoCoBorrower} data-id="element-545" />
            </SectionCard>

            {!noCoBorrower && <>
                <SectionCard title="Personal Information" data-id="element-546">
                  <div className="space-y-4" data-id="element-547">
                    <FieldGrid data-id="element-548">
                      <TextField label="Name" value={coBorrowerName} onChange={setCoBorrowerName} data-id="element-549" />
                      <YesNoToggle label="Married" value={coBorrowerMarried} onChange={setCoBorrowerMarried} data-id="element-550" />
                    </FieldGrid>
                    <FieldGrid data-id="element-551">
                      <TextField label="SSN" value={coBorrowerSsn} onChange={setCoBorrowerSsn} placeholder="XXX-XX-XXXX" data-id="element-552" />
                      <DateField label="Date of Birth" value={coBorrowerDob} onChange={setCoBorrowerDob} data-id="element-553" />
                    </FieldGrid>
                    <TextField label="Credit Score" value={coBorrowerCreditScore} onChange={setCoBorrowerCreditScore} data-id="element-554" />
                  </div>
                </SectionCard>

                <SectionCard title="Employment" data-id="element-555">
                  <div className="space-y-4" data-id="element-556">
                    <FieldGrid data-id="element-557">
                      <TextField label="Employer" value={coBorrowerEmployer} onChange={setCoBorrowerEmployer} data-id="element-558" />
                      <TextField label="Position" value={coBorrowerPosition} onChange={setCoBorrowerPosition} data-id="element-559" />
                    </FieldGrid>
                    <FieldGrid data-id="element-560">
                      <TextField label="Years Employed" value={coBorrowerYears} onChange={setCoBorrowerYears} data-id="element-561" />
                      <CurrencyField label="Annual Income" value={coBorrowerIncome} onChange={setCoBorrowerIncome} data-id="element-562" />
                    </FieldGrid>
                  </div>
                </SectionCard>

                <SectionCard title="Previous Employment" data-id="element-563">
                  <div className="space-y-4" data-id="element-564">
                    <FieldGrid data-id="element-565">
                      <TextField label="Employer" value={coBorrowerPrevEmployer} onChange={setCoBorrowerPrevEmployer} data-id="element-566" />
                      <TextField label="Position" value={coBorrowerPrevPosition} onChange={setCoBorrowerPrevPosition} data-id="element-567" />
                    </FieldGrid>
                    <FieldGrid data-id="element-568">
                      <TextField label="Years Employed" value={coBorrowerPrevYears} onChange={setCoBorrowerPrevYears} data-id="element-569" />
                      <CurrencyField label="Annual Income" value={coBorrowerPrevIncome} onChange={setCoBorrowerPrevIncome} data-id="element-570" />
                    </FieldGrid>
                  </div>
                </SectionCard>

                <SectionCard title="Additional Income" data-id="element-571">
                  <FieldGrid data-id="element-572">
                    <CurrencyField label="SSI" value={coBorrowerSsi} onChange={setCoBorrowerSsi} data-id="element-573" />
                    <CurrencyField label="Pension" value={coBorrowerPension} onChange={setCoBorrowerPension} data-id="element-574" />
                    <CurrencyField label="VA Disability" value={coBorrowerVaDisability} onChange={setCoBorrowerVaDisability} data-id="element-575" />
                    <CurrencyField label="Additional Income" value={coBorrowerAdditionalIncome} onChange={setCoBorrowerAdditionalIncome} data-id="element-576" />
                  </FieldGrid>
                  <div className="mt-4" data-id="element-577">
                    <TextField label="Next Pay Raise" value={coBorrowerNextPayRaise} onChange={setCoBorrowerNextPayRaise} data-id="element-578" />
                  </div>
                </SectionCard>
              </>}
          </div>;
      case 3:
        // Subject Property
        return <div className="space-y-6" data-id="element-579">
            <SectionCard title="Occupancy" data-id="element-580">
              <RadioGroup label="Property Use" value={occupancy} onChange={setOccupancy} options={[{
              value: 'Primary Residence',
              label: 'Primary Residence'
            }, {
              value: 'Second Home',
              label: 'Second Home'
            }, {
              value: 'Investment Property',
              label: 'Investment Property'
            }]} required data-id="element-581" />
            </SectionCard>

            <SectionCard title="Property Characteristics" data-id="element-582">
              <FieldGrid data-id="element-583">
                <TextField label="Year Built" value={yearBuilt} onChange={setYearBuilt} data-id="element-584" />
                <TextField label="Square Footage" value={squareFootage} onChange={setSquareFootage} data-id="element-585" />
              </FieldGrid>
            </SectionCard>

            <SectionCard title="Property Type" data-id="element-586">
              <div className="space-y-4" data-id="element-587">
                <RadioGroup label="Type" value={propertyType} onChange={setPropertyType} options={[{
                value: 'Condo',
                label: 'Condo'
              }, {
                value: 'Single Family Residence',
                label: 'Single Family Residence'
              }, {
                value: 'Townhouse',
                label: 'Townhouse'
              }, {
                value: 'Manufactured Home',
                label: 'Manufactured Home'
              }, {
                value: 'Mobile Home',
                label: 'Mobile Home'
              }, {
                value: '2-4 Unit',
                label: '2-4 Unit'
              }]} required data-id="element-588" />
                {propertyType === 'Condo' && <YesNoToggle label="Condo Approved?" value={condoApproved} onChange={setCondoApproved} data-id="element-589" />}
              </div>
            </SectionCard>
          </div>;
      case 4:
        // Current Mortgage
        return <div className="space-y-6" data-id="element-590">
            <SectionCard title="Current Mortgage Information" data-id="element-591">
              <div className="space-y-4" data-id="element-592">
                <FieldGrid data-id="element-593">
                  <DateField label="Last Refinance Date" value={lastRefinanceDate} onChange={setLastRefinanceDate} data-id="element-594" />
                  <TextField label="Purchase Year" value={purchaseYear} onChange={setPurchaseYear} data-id="element-595" />
                </FieldGrid>
                <FieldGrid data-id="element-596">
                  <CurrencyField label="Purchase Price" value={purchasePrice} onChange={setPurchasePrice} data-id="element-597" />
                  <CurrencyField label="Estimated Property Value" value={estimatedValue} onChange={setEstimatedValue} data-id="element-598" />
                </FieldGrid>
              </div>
            </SectionCard>

            <SectionCard title="Existing Loan" data-id="element-599">
              <div className="space-y-4" data-id="element-600">
                <TextField label="Mortgage Company" value={mortgageCompany} onChange={setMortgageCompany} data-id="element-601" />
                <FieldGrid data-id="element-602">
                  <CurrencyField label="Current Balance" value={currentBalance} onChange={setCurrentBalance} data-id="element-603" />
                  <CurrencyField label="Current Payment" value={currentPayment} onChange={setCurrentPayment} data-id="element-604" />
                </FieldGrid>
                <CurrencyField label="Client Estimated Value" value={clientEstimatedValue} onChange={setClientEstimatedValue} data-id="element-605" />
              </div>
            </SectionCard>

            <SectionCard title="Escrow Information" data-id="element-606">
              <div className="space-y-4" data-id="element-607">
                <YesNoToggle label="Escrowed" value={escrowed} onChange={setEscrowed} data-id="element-608" />
                <FieldGrid data-id="element-609">
                  <CurrencyField label="Property Taxes (Monthly)" value={propertyTaxes} onChange={setPropertyTaxes} data-id="element-610" />
                  <CurrencyField label="Insurance (Monthly)" value={insurance} onChange={setInsurance} data-id="element-611" />
                  <CurrencyField label="PMI (Monthly)" value={pmi} onChange={setPmi} data-id="element-612" />
                  <CurrencyField label="HOA (Monthly)" value={hoa} onChange={setHoa} data-id="element-613" />
                </FieldGrid>
              </div>
            </SectionCard>

            <SectionCard title="Current Loan Terms" data-id="element-614">
              <div className="space-y-4" data-id="element-615">
                <FieldGrid data-id="element-616">
                  <PercentField label="Interest Rate" value={interestRate} onChange={setInterestRate} data-id="element-617" />
                  <TextField label="Loan Term (Years)" value={loanTerm} onChange={setLoanTerm} data-id="element-618" />
                </FieldGrid>
                <RadioGroup label="Loan Structure" value={loanStructure} onChange={setLoanStructure} options={[{
                value: 'Fixed',
                label: 'Fixed'
              }, {
                value: 'Adjustable',
                label: 'Adjustable'
              }, {
                value: 'Interest Only',
                label: 'Interest Only'
              }]} layout="horizontal" data-id="element-619" />
                <RadioGroup label="Loan Type" value={loanType} onChange={setLoanType} options={[{
                value: 'FHA',
                label: 'FHA'
              }, {
                value: 'Conventional',
                label: 'Conventional'
              }, {
                value: 'VA',
                label: 'VA'
              }]} layout="horizontal" data-id="element-620" />
              </div>
            </SectionCard>

            <SectionCard title="Second Mortgage / HELOC" data-id="element-621">
              <div className="space-y-4" data-id="element-622">
                <YesNoToggle label="Has Second Mortgage?" value={hasSecondMortgage} onChange={setHasSecondMortgage} data-id="element-623" />
                {hasSecondMortgage && <>
                    <TextField label="Lender Name" value={secondLender} onChange={setSecondLender} data-id="element-624" />
                    <FieldGrid data-id="element-625">
                      <CurrencyField label="Balance" value={secondBalance} onChange={setSecondBalance} data-id="element-626" />
                      <TextField label="Year Opened" value={secondYearOpened} onChange={setSecondYearOpened} data-id="element-627" />
                    </FieldGrid>
                    <FieldGrid data-id="element-628">
                      <CurrencyField label="Payment" value={secondPayment} onChange={setSecondPayment} data-id="element-629" />
                      <PercentField label="Interest Rate" value={secondRate} onChange={setSecondRate} data-id="element-630" />
                    </FieldGrid>
                    <TextField label="Term" value={secondTerm} onChange={setSecondTerm} data-id="element-631" />
                    <RadioGroup label="Loan Structure" value={secondStructure} onChange={setSecondStructure} options={[{
                  value: 'Fixed',
                  label: 'Fixed'
                }, {
                  value: 'Adjustable',
                  label: 'Adjustable'
                }, {
                  value: 'Interest Only',
                  label: 'Interest Only'
                }]} layout="horizontal" data-id="element-632" />
                    <YesNoToggle label="Pulled Cash Out Within Last 12 Months?" value={helocCashOut} onChange={setHelocCashOut} data-id="element-633" />
                  </>}
              </div>
            </SectionCard>
          </div>;
      case 5:
        // Credit & Assets
        return <div className="space-y-6" data-id="element-634">
            <SectionCard title="Credit & Property Questions" data-id="element-635">
              <div className="space-y-4" data-id="element-636">
                <YesNoToggle label="Foreclosure Within Last 7 Years?" value={foreclosure} onChange={setForeclosure} data-id="element-637" />
                <YesNoToggle label="Bankruptcy Within Last 7 Years?" value={bankruptcy} onChange={setBankruptcy} data-id="element-638" />
                {bankruptcy && <RadioGroup label="Bankruptcy Chapter" value={bankruptcyChapter} onChange={setBankruptcyChapter} options={[{
                value: 'Chapter 7',
                label: 'Chapter 7'
              }, {
                value: 'Chapter 13',
                label: 'Chapter 13'
              }]} layout="horizontal" data-id="element-639" />}
                <YesNoToggle label="Child Support Obligation?" value={childSupport} onChange={setChildSupport} data-id="element-640" />
                <YesNoToggle label="Loan Modification?" value={loanModification} onChange={setLoanModification} data-id="element-641" />
                <YesNoToggle label="Deferred Interest?" value={deferredInterest} onChange={setDeferredInterest} data-id="element-642" />
                <YesNoToggle label="Solar Panels?" value={solarPanels} onChange={setSolarPanels} data-id="element-643" />
                <YesNoToggle label="Mortgage Forbearance?" value={forbearance} onChange={setForbearance} data-id="element-644" />
                <YesNoToggle label="Liens Attached To Property?" value={liens} onChange={setLiens} data-id="element-645" />
                <YesNoToggle label="Late Mortgage Payments?" value={lateMortgage} onChange={setLateMortgage} data-id="element-646" />
                <YesNoToggle label="Farm Activity?" value={farmActivity} onChange={setFarmActivity} data-id="element-647" />
                <YesNoToggle label="Reinstated?" value={reinstated} onChange={setReinstated} data-id="element-648" />
                <FieldGrid data-id="element-649">
                  <CurrencyField label="Amount Owed" value={amountOwed} onChange={setAmountOwed} data-id="element-650" />
                  <DateField label="Date Owed" value={dateOwed} onChange={setDateOwed} data-id="element-651" />
                </FieldGrid>
              </div>
            </SectionCard>

            <SectionCard title="Assets" data-id="element-652">
              <TextField label="Asset Summary" value={assetSummary} onChange={setAssetSummary} placeholder="Describe your assets..." data-id="element-653" />
            </SectionCard>

            <SectionCard title="VA Specific Information" data-id="element-654">
              <div className="space-y-4" data-id="element-655">
                <h4 className="font-medium text-gray-900" data-id="element-656">
                  VA Non-Living Relative
                </h4>
                <TextField label="Name" value={vaRelativeName} onChange={setVaRelativeName} data-id="element-657" />
                <TextField label="Address" value={vaRelativeAddress} onChange={setVaRelativeAddress} data-id="element-658" />
                <PhoneField label="Phone Number" value={vaRelativePhone} onChange={setVaRelativePhone} data-id="element-659" />
                <TextField label="Relationship" value={vaRelativeRelationship} onChange={setVaRelativeRelationship} data-id="element-660" />
              </div>
            </SectionCard>

            <SectionCard title="Water Source" data-id="element-661">
              <RadioGroup label="Water Source" value={waterSource} onChange={setWaterSource} options={[{
              value: 'Well Water',
              label: 'Well Water'
            }, {
              value: 'City Water',
              label: 'City Water'
            }]} layout="horizontal" data-id="element-662" />
            </SectionCard>
          </div>;
      case 6:
        // Review
        return <div className="space-y-6" data-id="element-663">
            <SectionCard data-id="element-664">
              <div className="text-center py-8" data-id="element-665">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-666">
                  Review Your Application
                </h2>
                <p className="text-gray-600 mb-6" data-id="element-667">
                  Please review all the information you've provided. You can go
                  back to any step to make changes.
                </p>
                <div className="bg-blue-50 rounded-lg p-6 text-left" data-id="element-668">
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-669">
                    Application Summary
                  </h3>
                  <div className="space-y-2 text-sm text-gray-700" data-id="element-670">
                    <p data-id="element-671">
                      <strong data-id="element-672">Borrower:</strong>{' '}
                      {borrowerName || 'Not provided'}
                    </p>
                    <p data-id="element-673">
                      <strong data-id="element-674">Email:</strong> {email || 'Not provided'}
                    </p>
                    <p data-id="element-675">
                      <strong data-id="element-676">Property Type:</strong>{' '}
                      {propertyType || 'Not provided'}
                    </p>
                    <p data-id="element-677">
                      <strong data-id="element-678">Current Balance:</strong>{' '}
                      {currentBalance ? `$${parseFloat(currentBalance).toLocaleString()}` : 'Not provided'}
                    </p>
                    <p data-id="element-679">
                      <strong data-id="element-680">Loan Type:</strong> {loanType || 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>;
      default:
        return null;
    }
  };
  return <WizardShell steps={STEPS} completionPercentage={completionPercentage} headerContent={<CrmHeader data-id="element-682" />} currentStep={currentStep} onStepChange={setCurrentStep} onBack={handleBack} onNext={handleNext} onSubmit={handleSubmit} canGoBack={currentStep > 0} canGoNext={true} isLastStep={currentStep === STEPS.length - 1} data-id="element-681">
      <div className="space-y-6 pb-24" data-id="element-683">
        {STEPS.map((step, index) => <div key={step.id} id={step.id} className="scroll-mt-24" data-id="element-684">
            <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-2" data-id="element-685">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900" data-id="element-686">
                {step.label}
              </h2>
              <div className="hidden lg:block text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full" data-id="element-687">
                Section {index + 1} of {STEPS.length}
              </div>
            </div>
            {getStepContent(index)}
          </div>)}
      </div>
    </WizardShell>;
}
export function RefinanceApplication() {
  return <ApplicationProvider data-id="element-688">
      <RefinanceApplicationContent data-id="element-689" />
    </ApplicationProvider>;
}